<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import AnalysisLaunchDialog from "./AnalysisLaunchDialog.vue";
import { usePlanAnalysis } from "@/composable/usePlanAnalysis";
import { useAnalysisLaunch } from "@/composable/useAnalysisLaunch";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";
import { useDeviceState } from "@/stores/DeviceState";

/**
 * Bouton « Analyser le plan » de l'onglet Observations (plan déjà rattaché à
 * un ouvrage) : en pied de l'onglet, ou centré sous le message de liste vide.
 * Il n'est proposé que tant qu'aucune analyse n'existe sur ce poste — la
 * relance se fait ensuite depuis l'onglet IA. Le clic ouvre la modale de
 * validation (unité du plan) ; la confirmation lance le pipeline complet
 * (scan + IA) sous l'animation de balayage, puis ouvre l'onglet IA.
 * L'analyse est un outil de bureau EN LIGNE : désactivée hors connexion (le
 * pipeline IA est obligatoire, pas de repli local).
 */

const { t } = useTranslation();
const state = usePlanAnalysisState();
const device = useDeviceState();
const { runAnalysis } = usePlanAnalysis();
const { showLaunchDialog, suggestedUnit, requestAnalysis, confirmLaunch } =
  useAnalysisLaunch(runAnalysis);

// `isOnline` vaut null tant que le premier check n'a pas répondu : on ne
// bloque que sur un hors-ligne avéré.
const isOffline = computed(() => device.isOnline === false);
</script>

<template>
  <div>
    <button
      type="button"
      class="analyze-plan-btn w-100 d-flex align-items-center justify-content-center gap-2"
      :disabled="state.isAnalyzing || isOffline"
      @click="requestAnalysis"
    >
      <span
        v-if="state.isAnalyzing"
        class="spinner-border spinner-border-sm"
        role="status"
      />
      <PhosphorIcon v-else name="sparkle" :size="16" weight="duotone" />
      <span>{{ t("canvas:analysis.analyzeButton.tooltip.title") }}</span>
    </button>
    <div v-if="isOffline" class="offline-hint d-flex align-items-center gap-1">
      <PhosphorIcon name="wifi-slash" :size="12" />
      {{ t("canvas:analysis.offlineHint") }}
    </div>
    <AnalysisLaunchDialog
      v-model="showLaunchDialog"
      :initial-unit="suggestedUnit"
      @confirm="confirmLaunch"
    />
  </div>
</template>

<style lang="scss" scoped>
.analyze-plan-btn {
  border: 1px solid var(--dr-report);
  border-radius: 8px;
  background: color-mix(in srgb, var(--dr-report) 18%, transparent);
  color: var(--dr-report-light);
  font-size: 0.85rem;
  font-weight: 500;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--dr-report) 32%, transparent);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .spinner-border {
    width: 14px;
    height: 14px;
    border-width: 2px;
  }
}

.offline-hint {
  font-size: 0.7rem;
  opacity: 0.65;
  margin-top: 4px;
  justify-content: center;
}
</style>
